import React from "react";
import { Row, Col, Card, ListGroup, Badge, Table, Nav } from "react-bootstrap";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

export default class ProfilingDashboard extends React.Component<any, any> {
  state = {
    activeGroup: "",
    selectedColumn: null as any,
    activeChart: null as string | null,
    activeLabel: null as string | null,
  };

  /** Group profiles by table (source_prefix) */
  extractGroups = () => {
    const profiles = this.props.profiling_results || [];
    const grouped: Record<string, any[]> = {};
    profiles.forEach((profile: any) => {
      const prefix = profile.source_prefix || "Unknown Table";
      if (!grouped[prefix]) grouped[prefix] = [];
      grouped[prefix].push(profile);
    });
    return Object.entries(grouped).map(([label, fields]) => ({
      label,
      fields,
    }));
  };

  /** Render left column list */
  renderColumnList = (fields: any[]) => {
    const { selectedColumn } = this.state;

    return (
      <ListGroup
        variant="flush"
        className="border-end"
        style={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        {fields.map((col: any) => {
          const completeness = 100 - col.null_percentage;
          const uniqueness = col.unique_values ? (col.unique_values / col.count) * 100 : 0;

          return (
            <ListGroup.Item
              key={col.column_name}
              action
              active={selectedColumn?.column_name === col.column_name}
              onClick={() =>
                this.setState({ selectedColumn: col, activeChart: null, activeLabel: null })
              }
              className="d-flex justify-content-between align-items-start"
            >
              <div>
                <div className="fw-bold">{col.column_name}</div>
                <div className="small text-muted">{col.data_type}</div>
                <div className="small text-muted">{col.recommended_type}</div>
              </div>
              <div className="text-end small">
                <div>
                  Completeness:{" "}
                  <Badge bg={completeness > 90 ? "success" : "warning"}>
                    {completeness.toFixed(1)}%
                  </Badge>
                </div>
                <div>
                  Uniqueness:{" "}
                  <Badge bg={uniqueness > 50 ? "info" : "secondary"}>
                    {uniqueness.toFixed(1)}%
                  </Badge>
                </div>
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    );
  };

  /** Handle chart slice/bar click */
  handleChartClick = (evt: any, elements: any, chartType: string, labels: string[]) => {
    if (!elements?.length) return;
    const index = elements[0].index;
    const label = labels[index];
    this.setState({ activeChart: chartType, activeLabel: label });
  };

  /** Render table for sample records */
  renderSampleTable = (records: any[], colName: string) => {
    if (!records || records.length === 0) {
      return <div className="text-muted">No records available</div>;
    }
    const headers = [colName, ...Object.keys(records[0]).filter((h) => h !== colName)];

    return (
      <Table striped bordered size="sm" className="align-middle">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((rec, i) => (
            <tr key={i}>
              {headers.map((h) => (
                <td key={h}>{rec[h] ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  /** Render right panel details */
  renderColumnDetails = (col: any) => {
    if (!col) {
      return <div className="text-muted p-4">Select a column to view profiling details</div>;
    }

    const recordStore = this.props.record_store || {};
    const { activeChart, activeLabel } = this.state;

    // Chart data
    const nullLabels = ["Null", "Non-Null"];
    const nullData = [col.null_non_null_samples.null_samples.length, col.null_non_null_samples.non_null_samples.length];

    const typeLabels = ["Int", "Float", "Date", "Boolean", "String"];
    const typeData = [
      col.value_type_percentages.int_pct,
      col.value_type_percentages.float_pct,
      col.value_type_percentages.date_pct,
      col.value_type_percentages.boolean_pct,
      col.value_type_percentages.string_pct,
    ];

    const lengthLabels = col.data_length_distribution.map((d: any) => d.length_range);
    const lengthData = col.data_length_distribution.map((d: any) => d.pct);

    const distinctLabels = Object.keys(col.distinct_values);
    const distinctData = Object.values(col.distinct_values);

    // Highlight logic: keep original fill color, use gold border on highlight
    const highlightBorder = "#FFD700";

    const nullPie = {
      labels: nullLabels,
      datasets: [
        {
          data: nullData,
          backgroundColor: [ "#e53e3e", "#38a169" ],
          borderColor: nullLabels.map((l) =>
            activeChart === "null" && activeLabel === l ? highlightBorder : "#fff"
          ),
          borderWidth: nullLabels.map((l) =>
            activeChart === "null" && activeLabel === l ? 4 : 1
          ),
        },
      ],
    };

    const typePie = {
      labels: typeLabels,
      datasets: [
        {
          data: typeData,
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
          borderColor: typeLabels.map((l) =>
            activeChart === "types" && activeLabel === l ? highlightBorder : "#fff"
          ),
          borderWidth: typeLabels.map((l) =>
            activeChart === "types" && activeLabel === l ? 4 : 1
          ),
        },
      ],
    };

    const lengthBar = {
      labels: lengthLabels,
      datasets: [
        {
          label: "% of Records",
          data: lengthData,
          backgroundColor: lengthLabels.map((l: string) =>
            activeChart === "length" && activeLabel === l ? highlightBorder : "#3182ce"
          ),
        },
      ],
    };

    const distinctBar = {
      labels: distinctLabels,
      datasets: [
        {
          label: "Count",
          data: distinctData,
          backgroundColor: distinctLabels.map((l) =>
            activeChart === "distinct" && activeLabel === l ? highlightBorder : "#805AD5"
          ),
        },
      ],
    };

    // Sample records for clicked slice
    let sampleRecords: any[] = [];
    if (activeChart === "null") {
      if (activeLabel === "Null") {
        sampleRecords = col.null_non_null_samples.null_samples.map((v: any) => recordStore[v] || {});
      } else if (activeLabel === "Non-Null") {
        sampleRecords = col.null_non_null_samples.non_null_samples.map((v: any) => recordStore[v] || {});
      }
    } else if (activeChart === "types") {
      const typeMap: any = col.value_type_record_samples || {};
      const slice = typeMap[activeLabel?.toLowerCase() + "_like"] || [];
      sampleRecords = slice.map((v: any) => recordStore[v] || {});
    } else if (activeChart === "length" && activeLabel) {
      const slice = (col.data_length_record_samples && col.data_length_record_samples[activeLabel]) || [];
      sampleRecords = slice.map((v: any) => recordStore[v] || {});
    } else if (activeChart === "distinct" && (activeLabel || activeLabel ==="")) {
      const slice = (col.distinct_values_samples && col.distinct_values_samples[activeLabel]) || [];
      sampleRecords = slice.map((v: any) => recordStore[v] || {});
    }

    return (
      <div className="p-3">
        {/* Charts row with horizontal scroll */}
        <div style={{ display: "flex", gap: "1rem", overflowX: "auto", paddingBottom: "0.5rem" }}>
          <Card style={{ minWidth: "380px" }}>
            <Card.Header className="text-center">Null vs Non-Null</Card.Header>
            <Card.Body style={{ height: "200px" }}>
              <Pie
                data={nullPie}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  onClick: (evt, el) => this.handleChartClick(evt, el, "null", nullLabels),
                }}
              />
            </Card.Body>
          </Card>

          <Card style={{ minWidth: "380px" }}>
            <Card.Header className="text-center">Value Type Percentages</Card.Header>
            <Card.Body style={{ height: "200px" }}>
              <Pie
                data={typePie}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  onClick: (evt, el) => this.handleChartClick(evt, el, "types", typeLabels),
                }}
              />
            </Card.Body>
          </Card>

          <Card style={{ minWidth: "380px" }}>
            <Card.Header className="text-center">Data Length Distribution</Card.Header>
            <Card.Body style={{ height: "200px" }}>
              <Bar
                data={lengthBar}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  onClick: (evt, el) => this.handleChartClick(evt, el, "length", lengthLabels),
                }}
              />
            </Card.Body>
          </Card>

          <Card style={{ minWidth: "480px" }}>
            <Card.Header className="text-center">Distinct Values</Card.Header>
            <Card.Body style={{ height: "200px" }}>
              <Bar
                data={distinctBar}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  onClick: (evt, el) => this.handleChartClick(evt, el, "distinct", distinctLabels),
                }}
              />
            </Card.Body>
          </Card>
        </div>

        {/* Records table for clicked chart slice */}
        {activeChart && (
          <Card className="mt-3">
            <Card.Header className="fw-bold">
              Sample Records — {activeChart} → {activeLabel}
            </Card.Header>
            <Card.Body style={{ maxHeight: "300px", overflowY: "auto" }}>
              {this.renderSampleTable(sampleRecords, col.column_name)}
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  /** Render vertical nav sidebar for tables */
  renderVerticalNav = (groups: any[], active: any) => {
    return (
        <Nav
        variant="pills"
        activeKey={active?.label}
        onSelect={(k) => this.setState({ activeGroup: k || "", selectedColumn: null })}
        className="flex-column border-end bg-light"
        style={{
            width: "60px",
            minHeight: "100vh",
            alignItems: "center",
            paddingTop: "1rem",
        }}
        >
        {groups.map((g) => (
            <Nav.Item
            key={g.label}
            style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                marginBottom: "1rem",
            }}
            >
            <Nav.Link
                eventKey={g.label}
                className="px-2 text-center"
                style={{
                fontSize: "12px",
                whiteSpace: "nowrap",
                padding: "0.5rem 0",
                }}
            >
                {g.label}
            </Nav.Link>
            </Nav.Item>
        ))}
        </Nav>
    );
  };


  render() {
    const groups = this.extractGroups();
    const { activeGroup } = this.state;
    const active = groups.find((g) => g.label === activeGroup) || groups[0];

    return (
      <div className="d-flex" style={{ height: "80vh" }}>
        {/* Left vertical nav */}
        {this.renderVerticalNav(groups, active)}

        {/* Middle: column list */}
        <div style={{ width: "320px", borderRight: "1px solid #dee2e6" ,  overflowY: "auto",
            height: "calc(100vh - 356px)"}}>
            <h6 className="p-2 bg-light">Columns ({active.label})</h6>
            {this.renderColumnList(active.fields)}
        </div>

        {/* Right: charts/details */}
        <div
            style={{
            width: "1130px",
            overflow: "auto",
            padding: "1rem",
            }}
        >
            {this.renderColumnDetails(this.state.selectedColumn)}
        </div>
        </div>

    );
  }
}
